import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useAiDictationStore from '../stores/aiDictationStore';
import StatusBar from '../components/StatusBar';
import NumberAdjuster from '../components/NumberAdjuster';

const AiDictation = () => {
  const navigate = useNavigate();
  const {
    selectedWords, generatedStory, dictationSettings,
    regenerateStory, removeWord,
    increaseReadingSpeed, decreaseReadingSpeed,
    increaseRepeatCount, decreaseRepeatCount,
    increaseSentenceInterval, decreaseSentenceInterval
  } = useAiDictationStore();

  // 获取朗读速度显示文本
  const getReadingSpeedText = () => {
    const speedMap = {
      'slow': '慢速',
      'normal': '正常',
      'fast': '快速'
    };
    return speedMap[dictationSettings.readingSpeed] || '正常';
  };

  return (
    <div className="min-h-screen bg-[#f9f9f9] text-[#333]">
      {/* Main Content */}
      <div className="p-5">
        {/* Navigation Header */}
        <div className="flex justify-between items-center mb-6">
          <Link to="/" className="text-blue-500">
            <i className="fa-solid fa-chevron-left mr-1"></i>
            返回
          </Link>
          <h2 className="text-xl font-bold">AI短文听写</h2>
          <div className="w-16"></div> {/* Spacer for alignment */}
        </div>

        {/* AI Story Description */}
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-5 mb-6 text-white shadow-md">
          <div className="flex items-center mb-3">
            <div className="bg-white/20 rounded-full p-2 mr-3">
              <i className="fa-solid fa-wand-magic-sparkles text-xl"></i>
            </div>
            <h3 className="font-bold text-lg">AI智能短文听写</h3>
          </div>
          <p className="text-white/90 text-sm mb-3">
            AI将把您选择的单词编织成一个有趣的短文，每个句子中包含一个目标单词，让听写更有趣！
          </p>
          <div className="text-xs bg-white/20 rounded-full px-3 py-1 inline-block">
            新功能
          </div>
        </div>

        {/* Word Source Selection */}
        <div className="mb-6">
          <h3 className="font-bold text-lg mb-3">选择单词来源</h3>
          <div className="grid grid-cols-1 gap-3">
            <div 
              className="bg-white rounded-xl p-4 shadow-sm flex items-center cursor-pointer"
              onClick={() => navigate('/ai-dictation/manual')}
            >
              <div className="rounded-full bg-indigo-100 p-3 mr-4">
                <i className="fa-solid fa-hand-point-up text-indigo-600"></i>
              </div>
              <div className="flex-1">
                <h4 className="font-medium">手动选择单词</h4>
                <p className="text-xs text-gray-500">手动选择要包含在短文中的单词</p>
              </div>
              <i className="fa-solid fa-chevron-right text-gray-400"></i>
            </div>
            
            <div 
              className="bg-white rounded-xl p-4 shadow-sm flex items-center cursor-pointer"
              onClick={() => navigate('/ai-dictation/textbook')}
            >
              <div className="rounded-full bg-green-100 p-3 mr-4">
                <i className="fa-solid fa-book text-green-600"></i>
              </div>
              <div className="flex-1">
                <h4 className="font-medium">课本单元单词</h4>
                <p className="text-xs text-gray-500">从课本单元中选择单词</p>
              </div>
              <i className="fa-solid fa-chevron-right text-gray-400"></i>
            </div>
            
            <div 
              className="bg-white rounded-xl p-4 shadow-sm flex items-center cursor-pointer"
              onClick={() => navigate('/ai-dictation/random')}
            >
              <div className="rounded-full bg-purple-100 p-3 mr-4">
                <i className="fa-solid fa-shuffle text-purple-600"></i>
              </div>
              <div className="flex-1">
                <h4 className="font-medium">随机单词组合</h4>
                <p className="text-xs text-gray-500">系统随机选择不同难度的单词</p>
              </div>
              <i className="fa-solid fa-chevron-right text-gray-400"></i>
            </div>
          </div>
        </div>

        {/* Selected Words */}
        <div className="mb-6">
          <h3 className="font-bold text-lg mb-3">已选单词</h3>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex flex-wrap gap-2 mb-4">
              {selectedWords.map((word) => (
                <div 
                  key={word.id}
                  className="word-chip px-3 py-2 rounded-full text-sm border border-indigo-300 bg-indigo-50 text-indigo-700 flex items-center"
                >
                  <span>{word.word}</span>
                  <button 
                    className="ml-1 text-indigo-400"
                    onClick={() => removeWord(word.id)}
                  >
                    <i className="fa-solid fa-xmark text-xs"></i>
                  </button>
                </div>
              ))}
            </div>
            <p className="text-gray-500 text-sm">
              已选择: <span className="font-medium">{selectedWords.length}</span> 个单词
            </p>
          </div>
        </div>

        {/* Story Preview */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-bold text-lg">短文预览</h3>
            <button 
              className="text-indigo-600 text-sm font-medium flex items-center"
              onClick={regenerateStory}
              disabled={generatedStory.isLoading}
            >
              {generatedStory.isLoading ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin mr-1"></i>
                  生成中...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-arrows-rotate mr-1"></i>
                  重新生成
                </>
              )}
            </button>
          </div>
          <div className="story-card mb-3">
            {generatedStory.isLoading ? (
              <div className="flex items-center justify-center py-8">
                <i className="fa-solid fa-spinner fa-spin text-indigo-500 text-2xl"></i>
              </div>
            ) : (
              <>
                <p className="mb-3 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: generatedStory.text }}></p>
                <div className="text-xs text-gray-500">
                  <i className="fa-solid fa-circle-info mr-1"></i>
                  短文包含了所有选择的单词，每个句子一个单词
                </div>
              </>
            )}
          </div>
          <div className="flex items-center justify-center py-2 px-4 bg-indigo-50 rounded-xl text-indigo-700 text-sm">
            <i className="fa-solid fa-volume-high mr-2"></i>
            点击开始后将朗读整个短文进行听写
          </div>
        </div>

        {/* Dictation Settings */}
        <div className="mb-8">
          <h3 className="font-bold text-lg mb-3">听写设置</h3>
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-3 border-b border-gray-100 flex justify-between items-center">
              <div className="font-medium">朗读速度</div>
              <div className="flex items-center space-x-3">
                <button 
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
                  onClick={decreaseReadingSpeed}
                >
                  <i className="fa-solid fa-minus text-gray-500 text-xs"></i>
                </button>
                <span className="font-medium">{getReadingSpeedText()}</span>
                <button 
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
                  onClick={increaseReadingSpeed}
                >
                  <i className="fa-solid fa-plus text-gray-500 text-xs"></i>
                </button>
              </div>
            </div>
            <div className="p-3 border-b border-gray-100 flex justify-between items-center">
              <div className="font-medium">重复次数</div>
              <NumberAdjuster 
                value={dictationSettings.repeatCount}
                unit="次"
                onDecrease={decreaseRepeatCount}
                onIncrease={increaseRepeatCount}
              />
            </div>
            <div className="p-3 flex justify-between items-center">
              <div className="font-medium">句子间隔</div>
              <NumberAdjuster 
                value={dictationSettings.sentenceInterval}
                unit="秒"
                onDecrease={decreaseSentenceInterval}
                onIncrease={increaseSentenceInterval}
              />
            </div>
          </div>
        </div>

        {/* Start Button */}
        <button 
          className="w-full bg-indigo-600 text-white py-4 rounded-xl font-bold text-lg shadow-md flex items-center justify-center"
          onClick={() => navigate('/ai-dictation/start')}
        >
          <i className="fa-solid fa-play mr-2"></i>
          开始听写
        </button>
      </div>
    </div>
  );
};

export default AiDictation;